/**
 * Version monitor — detects backend deploys and shows a gentle update toast
 * so users stay in sync without interrupting in-progress games.
 *
 * Strategy:
 *  1. On first load, fetch /api/version and remember it.
 *  2. Poll every 90 seconds.
 *  3. When the server version differs AND the user is not in the middle of a
 *     game, show a one-click "Refresh to update" toast.
 *  4. If the user is in a game, defer the prompt until the game ends so we
 *     never interrupt play.
 *  5. Subscriber safety: refreshing preserves the auth cookie/token, so paying
 *     users don't lose access.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './VersionMonitor.css';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 90_000; // 90 seconds

const VersionMonitor = ({ isInGame = false }) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const initialVersionRef = useRef(null);
  const deferredRef = useRef(false);

  const fetchVersion = useCallback(async () => {
    try {
      // Cache-bust so intermediaries never serve a stale version payload
      const resp = await fetch(`${API}/api/version?t=${Date.now()}`, { cache: 'no-store' });
      if (!resp.ok) return null;
      const data = await resp.json();
      return data.version || null;
    } catch {
      return null;
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    let cancelled = false;
    fetchVersion().then(v => {
      if (!cancelled && v) initialVersionRef.current = v;
    });
    return () => { cancelled = true; };
  }, [fetchVersion]);

  // Periodic poll (fallback — WebSocket broadcast is the primary channel)
  useEffect(() => {
    const interval = setInterval(async () => {
      const current = await fetchVersion();
      if (!current || !initialVersionRef.current) return;
      if (current !== initialVersionRef.current) {
        if (isInGame) {
          // Defer — will trigger after the game ends
          deferredRef.current = true;
        } else {
          setUpdateAvailable(true);
        }
      }
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchVersion, isInGame]);

  // Listen for instant deploy broadcasts from the lobby WebSocket. When a
  // new server_version arrives, surface the toast immediately (respecting
  // the in-game deferral rule). Defensive: never reload directly, never
  // interrupt a game in progress.
  useEffect(() => {
    const onBroadcast = (e) => {
      const serverVersion = e?.detail?.version;
      if (!serverVersion) return;
      // First broadcast ever sets our baseline if we had none yet
      if (!initialVersionRef.current) {
        initialVersionRef.current = serverVersion;
        return;
      }
      if (serverVersion === initialVersionRef.current) return;
      if (isInGame) {
        deferredRef.current = true;
      } else {
        setUpdateAvailable(true);
      }
    };
    window.addEventListener('chessKingdomServerVersion', onBroadcast);
    return () => window.removeEventListener('chessKingdomServerVersion', onBroadcast);
  }, [isInGame]);

  // When user exits a game, surface any deferred update prompt
  useEffect(() => {
    if (!isInGame && deferredRef.current) {
      setUpdateAvailable(true);
      deferredRef.current = false;
    }
  }, [isInGame]);

  const handleRefresh = () => {
    // Best-effort: unregister any stale service workers before reloading so
    // the new bundle isn't intercepted by the old cache.
    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker.getRegistrations) {
        navigator.serviceWorker.getRegistrations().then(regs => {
          regs.forEach(r => r.unregister());
        }).catch(() => {});
      }
    } catch { /* ignore */ }
    // Hard reload — ignores memory cache for the top-level HTML
    window.location.reload();
  };

  if (!updateAvailable || dismissed) return null;

  // Portal to body so the toast is independent of the active screen tree
  return createPortal(
    <div className="version-monitor-toast" role="alert" data-testid="version-update-toast">
      <span className="version-monitor-dot" aria-hidden="true" />
      <div className="version-monitor-body">
        <strong>New update available</strong>
        <span>Refresh to get the latest Chess Kingdom experience. Your session and subscription will be kept.</span>
      </div>
      <div className="version-monitor-actions">
        <button
          className="version-monitor-btn-primary"
          onClick={handleRefresh}
          data-testid="version-update-refresh"
        >
          Refresh now
        </button>
        <button
          className="version-monitor-btn-secondary"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss update notification"
          data-testid="version-update-dismiss"
        >
          Later
        </button>
      </div>
    </div>,
    document.body
  );
};

export default VersionMonitor;
